import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-testimony-video',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        TableModule,
        DialogModule,
        InputTextModule,
        ToastModule,
        ConfirmDialogModule
    ],
    providers: [MessageService, ConfirmationService],
    template: `
        <div class="mx-auto px-6 py-8">
            <div class="flex justify-between items-center mb-6">
                <div>
                    <h1 class="text-3xl font-bold text-gray-900">Kelola Testimoni Video</h1>
                    <p class="text-gray-600">Daftar testimoni video dari customer</p>
                </div>
                <p-button
                    (onClick)="openNew()"
                    severity="success"
                    icon="pi pi-plus"
                    label="Tambah Testimoni"
                    size="large">
                </p-button>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <p-table
                    [value]="testimonies"
                    [loading]="loading"
                    [paginator]="true"
                    [rows]="10"
                    [showCurrentPageReport]="true"
                    currentPageReportTemplate="Menampilkan {first} sampai {last} dari {totalRecords} testimoni"
                    [rowsPerPageOptions]="[5, 10, 25]"
                    styleClass="p-datatable-striped">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Nama</th>
                            <th>URL Video</th>
                            <th>Aksi</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-testimony>
                        <tr>
                            <td>{{ testimony.name }}</td>
                            <td>
                                <a [href]="testimony.url" target="_blank" class="text-blue-600 hover:text-blue-800">
                                    {{ testimony.url }}
                                </a>
                            </td>
                            <td>
                                <p-button
                                    (onClick)="editTestimony(testimony)"
                                    icon="pi pi-pencil"
                                    severity="info"
                                    size="small"
                                    class="mr-2">
                                </p-button>
                                <p-button
                                    (onClick)="deleteTestimony(testimony)"
                                    icon="pi pi-trash"
                                    severity="danger"
                                    size="small">
                                </p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="3" class="text-center py-8">
                                <i class="pi pi-video text-4xl text-gray-400 mb-4 block"></i>
                                <p class="text-gray-500">Belum ada testimoni video</p>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        </div>

        <!-- Dialog for Add/Edit -->
        <p-dialog
            [(visible)]="testimonyDialog"
            [style]="{width: '450px'}"
            [header]="editMode ? 'Edit Testimoni' : 'Tambah Testimoni Baru'"
            [modal]="true"
            styleClass="p-fluid">
            <ng-template pTemplate="content">
                <div class="field">
                    <label for="name">Nama Testimoni</label>
                    <input
                        pInputText
                        id="name"
                        [(ngModel)]="testimony.name"
                        required="true"
                        autofocus
                        class="w-full" />
                </div>
                <div class="field mt-4">
                    <label for="url">URL Video</label>
                    <input
                        pInputText
                        id="url"
                        [(ngModel)]="testimony.url"
                        required="true"
                        placeholder="https://www.youtube.com/watch?v=..."
                        class="w-full" />
                </div>
            </ng-template>
            <ng-template pTemplate="footer">
                <p-button
                    (onClick)="hideDialog()"
                    label="Batal"
                    icon="pi pi-times"
                    severity="secondary">
                </p-button>
                <p-button
                    (onClick)="saveTestimony()"
                    label="Simpan"
                    icon="pi pi-check"
                    [loading]="saving">
                </p-button>
            </ng-template>
        </p-dialog>

        <p-confirmDialog></p-confirmDialog>
        <p-toast></p-toast>
    `
})
export class TestimonyVideo implements OnInit {
    testimonies: any[] = [];
    testimony: any = {};
    testimonyDialog = false;
    editMode = false;
    loading = false;
    saving = false;

    constructor(
        private userService: UserService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {}

    ngOnInit() {
        this.loadTestimonies();
    }

    loadTestimonies() {
        this.loading = true;
        this.userService.getTestimonyVideos().then(
            (response) => {
                this.testimonies = response.data || response || [];
                this.loading = false;
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat data testimoni'
                });
                this.loading = false;
            }
        );
    }

    openNew() {
        this.testimony = {};
        this.editMode = false;
        this.testimonyDialog = true;
    }

    editTestimony(testimony: any) {
        this.testimony = { ...testimony };
        this.editMode = true;
        this.testimonyDialog = true;
    }

    hideDialog() {
        this.testimonyDialog = false;
        this.testimony = {};
    }

    saveTestimony() {
        if (!this.testimony.name || !this.testimony.url) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Peringatan',
                detail: 'Nama dan URL video harus diisi'
            });
            return;
        }

        this.saving = true;
        const promise = this.editMode
            ? this.userService.updateTestimonyVideo(this.testimony)
            : this.userService.addTestimonyVideo(this.testimony);

        promise.then(
            () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: this.editMode ? 'Testimoni berhasil diperbarui' : 'Testimoni berhasil ditambahkan'
                });
                this.hideDialog();
                this.loadTestimonies();
                this.saving = false;
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal menyimpan testimoni'
                });
                this.saving = false;
            }
        );
    }

    deleteTestimony(testimony: any) {
        this.confirmationService.confirm({
            message: `Apakah Anda yakin ingin menghapus testimoni "${testimony.name}"?`,
            header: 'Konfirmasi Hapus',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Ya, Hapus',
            rejectLabel: 'Batal',
            accept: () => {
                this.userService.deleteTestimonyVideo({ id: testimony.id }).then(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: 'Testimoni berhasil dihapus'
                        });
                        this.loadTestimonies();
                    },
                    (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Gagal menghapus testimoni'
                        });
                    }
                );
            }
        });
    }
}